import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, CheckCircle2, ShieldCheck, Star, Loader2, ChevronRight, ArrowLeft } from "lucide-react";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Checkbox } from "@/components/ui/checkbox";
import Seo from "@/components/Seo";

function PayPalCheckout({ onSuccess, billing }) {
  return (
    <PayPalButtons
      style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal", height: 48 }}
      forceReRender={[JSON.stringify(billing)]}
      createOrder={async () => {
        const { data } = await api.post("/paypal/create-order", { billing_address: billing, withdrawal_consent: true });
        if (data.already_paid) {
          onSuccess();
          throw new Error("already_paid");
        }
        return data.id;
      }}
      onApprove={async (data) => {
        try {
          const { data: cap } = await api.post("/paypal/capture-order", { order_id: data.orderID });
          if (cap.status === "COMPLETED") {
            onSuccess();
          } else {
            toast.error("Zahlung nicht abgeschlossen.");
          }
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Zahlung konnte nicht bestätigt werden.");
        }
      }}
      onError={(err) => {
        console.error("PayPal error:", err);
        toast.error("Ein Fehler beim PayPal-Checkout ist aufgetreten. Bitte erneut versuchen.");
      }}
      onCancel={() => {
        toast.info("Zahlung abgebrochen.");
      }}
    />
  );
}

const emptyBilling = { full_name: "", street: "", postal_code: "", city: "", country: "Deutschland" };

function BillingForm({ initial, onSubmit }) {
  const [f, setF] = useState({ ...emptyBilling, ...initial });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.full_name.trim() && f.street.trim() && f.postal_code.trim() && f.city.trim();
  const submit = (e) => { e.preventDefault(); if (valid) onSubmit(f); };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="billing-form">
      <div>
        <Label htmlFor="bl-name">Vollständiger Name *</Label>
        <Input id="bl-name" required value={f.full_name} onChange={set("full_name")} className="rounded-xl mt-1.5 h-11" placeholder="Vorname Nachname" data-testid="billing-name" />
      </div>
      <div>
        <Label htmlFor="bl-street">Straße und Hausnummer *</Label>
        <Input id="bl-street" required value={f.street} onChange={set("street")} className="rounded-xl mt-1.5 h-11" placeholder="Musterstraße 1" data-testid="billing-street" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label htmlFor="bl-plz">PLZ *</Label>
          <Input id="bl-plz" required value={f.postal_code} onChange={set("postal_code")} className="rounded-xl mt-1.5 h-11" placeholder="12345" data-testid="billing-postal" />
        </div>
        <div className="col-span-2">
          <Label htmlFor="bl-city">Ort *</Label>
          <Input id="bl-city" required value={f.city} onChange={set("city")} className="rounded-xl mt-1.5 h-11" placeholder="Musterstadt" data-testid="billing-city" />
        </div>
      </div>
      <div>
        <Label htmlFor="bl-country">Land</Label>
        <Input id="bl-country" value={f.country} onChange={set("country")} className="rounded-xl mt-1.5 h-11" placeholder="Deutschland" data-testid="billing-country" />
      </div>
      <Button type="submit" disabled={!valid} className="w-full rounded-full h-12 bg-slate-900 hover:bg-slate-800 text-white btn-lift disabled:opacity-40" data-testid="billing-submit">
        Weiter zur Zahlung <ChevronRight className="w-4 h-4 ml-1" />
      </Button>
      <p className="text-xs text-slate-500 text-center">Die Angaben werden nur für die Rechnung verwendet.</p>
    </form>
  );
}

export default function Paywall() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [mockProcessing, setMockProcessing] = useState(false);
  const [billing, setBilling] = useState(null); // stored address (persists across edit)
  const [editing, setEditing] = useState(false); // when true, show Step 1 even if billing is set
  const [consent, setConsent] = useState(false); // AGB §6 - Widerrufsrecht erlischt bei Ausführung

  useEffect(() => {
    api.get("/paypal/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && user && user !== false && user.has_access) {
      nav("/plan");
    }
  }, [user, loading, nav]);

  const onPaymentSuccess = async () => {
    toast.success("Zahlung erfolgreich - Zugang freigeschaltet!");
    await refresh();
    nav("/plan");
  };

  const startMockPayment = async () => {
    if (!user || user === false) { nav("/register"); return; }
    if (!billing) { toast.error("Bitte zuerst die Rechnungsadresse eingeben."); return; }
    if (!consent) { toast.error("Bitte bestätige den Hinweis zum Widerrufsrecht."); return; }
    setMockProcessing(true);
    try {
      const { data: order } = await api.post("/paypal/create-order", { billing_address: billing, withdrawal_consent: true });
      if (order.already_paid) { await refresh(); nav("/plan"); return; }
      const { data: cap } = await api.post("/paypal/capture-order", { order_id: order.id });
      if (cap.status === "COMPLETED") await onPaymentSuccess();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Zahlung fehlgeschlagen.");
    } finally {
      setMockProcessing(false);
    }
  };

  const isMock = cfg?.mock;
  const clientId = cfg?.client_id;
  const isGuest = user === false;

  return (
    <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
      <Seo
        title="12-Wochen-Ernährungsplan freischalten"
        description="Der strukturierte 12-Wochen-Plan zur Wiederherstellung der Lactose-Toleranz. Einmalzahlung 19,99 EUR dauerhafter Zugang."
        path="/paywall"
        keywords="Lactoseintoleranz Ernährungsplan kaufen, 12 Wochen Programm"
      />
      <div className="grid lg:grid-cols-12 gap-12">
        {/* Left: Value */}
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Premium Ernährungsplan</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">Dein 12-Wochen-Plan zur Lactose-Toleranz</h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Strukturiert. Alltagstauglich. Wissenschaftlich fundiert. Ein einmaliger Kauf - dauerhafter Zugang zu allen Rezepten und Wochen-Modulen.
          </p>

          <div className="relative mt-10 rounded-3xl overflow-hidden border border-slate-100">
            <div className="blur-content p-8 bg-white">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[1,2,3,4].map((w) => (
                  <div key={w} className="rounded-xl bg-slate-50 p-4">
                    <div className="text-xs text-emerald-700 font-semibold">Woche {w}</div>
                    <div className="font-semibold text-slate-900 mt-1">Aufbau-Phase</div>
                    <div className="text-xs text-slate-500 mt-2">3 Rezepte</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 space-y-3">
                {["Sanfter Einstieg mit fermentierten Produkten","Aufbau mit gereiften Käsesorten","Milch schrittweise integrieren"].map((t,i) => (
                  <div key={i} className="h-4 bg-slate-100 rounded-full w-3/4" />
                ))}
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent flex items-end justify-center pb-8">
              <div className="flex items-center gap-2 text-slate-700 bg-white/90 rounded-full px-4 py-2 shadow border border-slate-100">
                <Lock className="w-4 h-4" /> Inhalt gesperrt
              </div>
            </div>
          </div>

          <ul className="mt-8 space-y-3">
            {[
              "12 Wochen strukturierter Aufbau in 5 Phasen (1-2 · 3-4 · 5-6 · 7-8 · 9-12)",
              "16 alltagstaugliche Rezepte mit Lactose-Angaben",
              "Klare Steigerungslogik - keine Rätsel, keine Diäten",
              "Einmalzahlung. Dauerhafter Zugang. Keine Abos.",
            ].map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-slate-700">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> {f}
              </li>
            ))}
          </ul>
        </div>

        {/* Right: Payment card */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-24">
            <Card className="p-8 rounded-3xl border-slate-100 bg-white shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)]" data-testid="paywall-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Einmalzahlung</div>
                  <div className="text-4xl font-bold text-slate-900 mt-1">19,99 €</div>
                  <div className="text-sm text-slate-500 mt-1">Dauerhafter Zugang · Kein Abo</div>
                </div>
                <div className="flex items-center gap-1 text-amber-500">
                  <Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" /><Star className="w-4 h-4 fill-current" />
                </div>
              </div>

              <div className="my-6 h-px bg-slate-100" />

              {isGuest ? (
                <>
                  <p className="text-sm text-slate-500 mb-4">Bitte melde dich an oder erstelle ein Konto, um die Zahlung durchzuführen.</p>
                  <div className="flex gap-3">
                    <Link to="/register" className="flex-1" data-testid="paywall-register">
                      <Button className="w-full rounded-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white btn-lift">Konto erstellen</Button>
                    </Link>
                    <Link to="/login" className="flex-1" data-testid="paywall-login">
                      <Button variant="outline" className="w-full rounded-full h-12 btn-lift">Anmelden</Button>
                    </Link>
                  </div>
                </>
              ) : !billing || editing ? (
                <>
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">Schritt 1 von 2 · Rechnungsadresse</div>
                  <p className="text-sm text-slate-600 mb-4">Für die ordentliche Rechnung nach §14 UStG benötigen wir kurz deine Anschrift.</p>
                  <BillingForm
                    initial={billing || { full_name: user?.name || "" }}
                    onSubmit={(f) => { setBilling(f); setEditing(false); }}
                  />
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Schritt 2 von 2 · Zahlung</div>
                    <button onClick={() => setEditing(true)} className="text-xs text-emerald-700 hover:text-emerald-800 flex items-center gap-1" data-testid="billing-edit">
                      <ArrowLeft className="w-3 h-3" /> Adresse ändern
                    </button>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-100 text-xs text-slate-600 mb-5" data-testid="billing-summary">
                    <div className="font-semibold text-slate-900">{billing.full_name}</div>
                    <div>{billing.street}</div>
                    <div>{billing.postal_code} {billing.city}</div>
                    {billing.country && <div>{billing.country}</div>}
                  </div>

                  <label className="flex gap-3 items-start p-4 rounded-xl border border-slate-200 hover:border-emerald-300 mb-5 cursor-pointer transition-colors" data-testid="consent-label">
                    <Checkbox
                      id="withdrawal-consent"
                      checked={consent}
                      onCheckedChange={(v) => setConsent(v === true)}
                      className="mt-0.5"
                      data-testid="consent-checkbox"
                    />
                    <span className="text-xs text-slate-700 leading-relaxed">
                      Ich verlange ausdrücklich, dass mit der Ausführung des Vertrags vor Ablauf der Widerrufsfrist begonnen wird. Mir ist bekannt, dass mein Widerrufsrecht gemäß <Link to="/agb" className="text-emerald-700 hover:underline" target="_blank" rel="noreferrer">§ 6 der AGB</Link> mit Beginn der Ausführung erlischt.
                    </span>
                  </label>

                  {isMock ? (
                    <>
                      <Button
                        onClick={startMockPayment}
                        disabled={mockProcessing || !consent}
                        className="w-full rounded-full h-14 text-base font-semibold bg-[#FFC439] hover:bg-[#FFB300] text-slate-900 btn-lift disabled:opacity-40"
                        data-testid="paypal-buy-button"
                      >
                        {mockProcessing ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Wird verarbeitet...</>) : "Mit PayPal bezahlen (Test)"}
                      </Button>
                      <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-800" data-testid="paypal-mock-notice">
                        <strong>Testmodus:</strong> Zahlung wird simuliert.
                      </div>
                    </>
                  ) : clientId ? (
                    <div data-testid="paypal-live-buttons" className={consent ? "" : "opacity-40 pointer-events-none"} aria-disabled={!consent}>
                      <PayPalScriptProvider options={{ clientId: clientId, currency: "EUR", intent: "capture" }}>
                        <PayPalCheckout onSuccess={onPaymentSuccess} billing={billing} />
                      </PayPalScriptProvider>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" /> PayPal wird geladen...
                    </div>
                  )}
                  {!consent && !isMock && clientId && (
                    <p className="text-xs text-slate-500 mt-3 text-center" data-testid="consent-hint">Bitte zuerst den Hinweis zum Widerrufsrecht bestätigen, um die Zahlung freizuschalten.</p>
                  )}
                </>
              )}

              <div className="mt-6 grid grid-cols-2 gap-3 text-xs text-slate-500">
                <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Sichere Zahlung</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> 14-Tage-Widerruf</div>
              </div>

              {isGuest && (
                <p className="text-xs text-slate-500 mt-5 text-center">
                  Schon Konto? <Link to="/login" className="text-emerald-700 hover:underline">Anmelden</Link>
                </p>
              )}
            </Card>
            <ImagePlaceholder label="Ernährungsplan Vorschau" ratio="aspect-[4/3]" className="mt-6" src="https://images.pexels.com/photos/4929677/pexels-photo-4929677.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" />
          </div>
        </div>
      </div>
    </div>
  );
}
